/**
 * WI Hide Variant Option
 *
 * Removes a named variant option (e.g. "Cycle") from the variant picker as a
 * selectable control, while keeping the product priceable.
 *
 * Usage: <wi-hide-variant-option option="Cycle"></wi-hide-variant-option>
 */
class WiHideVariantOption extends HTMLElement {
  /** @type {MutationObserver | null} */
  #observer = null;

  /** @type {Element | null} */
  #picker = null;

  /** @type {boolean} */
  #pending = false;

  connectedCallback() {
    const picker = this.#findPicker();
    if (picker) {
      this.#start(picker);
    } else {
      this.#waitForPicker();
    }
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
  }

  /** @returns {string} */
  get #optionName() {
    return (this.getAttribute('option') || '').trim().toLowerCase();
  }

  /** @returns {Element | null} */
  #findPicker() {
    return (
      this.closest('product-information')?.querySelector('variant-picker') ??
      document.querySelector('variant-picker[data-template-product-match="true"]') ??
      document.querySelector('variant-picker')
    );
  }

  #waitForPicker() {
    const observer = new MutationObserver(() => {
      const picker = this.#findPicker();
      if (!picker) return;
      observer.disconnect();
      this.#start(picker);
    });
    observer.observe(document.body, { childList: true, subtree: true });
    this.#observer = observer;
  }

  /** @param {Element} picker */
  #start(picker) {
    this.#observer?.disconnect();
    this.#picker = picker;
    this.#apply();
    // Re-reduce after the picker morphs its full markup back in on each change.
    this.#observer = new MutationObserver(() => this.#scheduleApply());
    this.#observer.observe(picker, { childList: true, subtree: true, attributes: true });
  }

  #scheduleApply() {
    if (this.#pending) return;
    this.#pending = true;
    queueMicrotask(() => {
      this.#pending = false;
      this.#apply();
    });
  }

  /**
   * Reduce the target option's fieldset to a single hidden carrier input.
   */
  #apply() {
    const fieldset = this.#findOption();
    if (!fieldset) return;

    const checked = fieldset.querySelector('input:checked');
    if (!checked) return;

    const carrier = document.createElement('input');
    carrier.type = 'radio';
    carrier.checked = true;
    carrier.value = checked.value;
    carrier.style.display = 'none';
    const name = checked.getAttribute('name');
    if (name) carrier.setAttribute('name', name);
    carrier.dataset.optionValueId = checked.dataset.optionValueId ?? '';
    if (checked.dataset.variantId) carrier.dataset.variantId = checked.dataset.variantId;

    // Drop the legend + all selectable buttons; keep only the carrier.
    fieldset.replaceChildren(carrier);
    fieldset.removeAttribute('class');
    fieldset.style.border = '0';
    fieldset.style.margin = '0';
    fieldset.style.padding = '0';
  }

  /** @returns {HTMLElement | null} */
  #findOption() {
    const name = this.#optionName;
    if (!this.#picker || !name) return null;

    const options = this.#picker.querySelectorAll('.variant-option');
    for (const option of options) {
      if (option instanceof HTMLElement && this.#nameOf(option) === name) return option;
    }
    return null;
  }

  /**
   * Reads an option's display name from its legend (buttons/swatches) or its
   * leading text node (single-value option), ignoring nested value spans.
   * @param {Element} option
   * @returns {string}
   */
  #nameOf(option) {
    const source = option.querySelector(':scope > legend') ?? option;
    for (const node of source.childNodes) {
      if (node.nodeType !== Node.TEXT_NODE) continue;
      const text = node.textContent?.trim();
      if (text) return text.toLowerCase();
    }
    return '';
  }
}

if (!customElements.get('wi-hide-variant-option')) {
  customElements.define('wi-hide-variant-option', WiHideVariantOption);
}
